import React, { useCallback, useRef, useState } from 'react';
import { Download, Share2, Mail, Copy, Check, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import { trackRoomEvent } from '../../lib/roomEvents';
import { brandName } from '../../hooks/useCompanyBrand';


/* Floating action bar for downloading / sharing the current shade preview.
   Wraps the preview element and captures it as a branded PNG. */
export const SharePreview = ({ children, selectedColor, productType, config, className = '', hideBar = false }) => {
  const captureRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const colorName = selectedColor?.name || 'Shade';
  const productLabel = (productType || 'roller-shades').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  /* Capture the preview DOM as a canvas, add branding footer, return a Blob */
  const capture = useCallback(async () => {
    const el = captureRef.current;
    if (!el) return null;
    setCapturing(true);
    try {
      const canvas = await html2canvas(el, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#f5f5f4',
        scale: 2,
        logging: false,
        imageTimeout: 8000,
      });

      /* Add branding footer */
      const footer = 56;
      const out = document.createElement('canvas');
      out.width = canvas.width;
      out.height = canvas.height + footer;
      const ctx = out.getContext('2d');
      ctx.drawImage(canvas, 0, 0);

      /* Footer background */
      ctx.fillStyle = '#171717';
      ctx.fillRect(0, canvas.height, out.width, footer);

      /* Left: business name */
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 22px system-ui, sans-serif';
      ctx.fillText(brandName(), 20, canvas.height + 34);

      /* Right: fabric details */
      ctx.fillStyle = '#a3a3a3';
      ctx.font = '16px system-ui, sans-serif';
      const details = `${colorName} | ${productLabel}`;
      const tw = ctx.measureText(details).width;
      ctx.fillText(details, out.width - tw - 20, canvas.height + 34);

      return new Promise(resolve => out.toBlob(resolve, 'image/png'));
    } catch (err) {
      console.error('Capture failed', err);
      toast.error('Could not capture preview');
      return null;
    } finally {
      setCapturing(false);
    }
  }, [colorName, productLabel]);

  /* Download as PNG */
  const handleDownload = useCallback(async () => {
    const blob = await capture();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `apollo-shades-${colorName.toLowerCase().replace(/\s+/g, '-')}-${productType || 'shade'}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Preview saved');
    setShowMenu(false);
  }, [capture, colorName, productType]);

  const currentRoom = () => new URLSearchParams(window.location.search).get('room') || selectedColor?.default_room || null;
  const track = () => trackRoomEvent('share', selectedColor?.id, currentRoom(), productType);

  /* Shareable room look: server-rendered link with a room-picture preview for WhatsApp / iMessage */
  const shareUrl = useCallback(() => {
    const type = productType || 'roller-shades';
    if (!selectedColor?.id) return `${window.location.origin}/products/${type}`;
    const params = new URLSearchParams({ fabric: selectedColor.id });
    const room = new URLSearchParams(window.location.search).get('room');
    if (room) params.set('room', room);
    return `${window.location.origin}/api/share/p/${type}?${params}`;
  }, [selectedColor, productType]);

  /* Web Share API (mobile/desktop) */
  const handleShare = useCallback(async () => {
    const blob = await capture();
    if (!blob) return;
    const file = new File([blob], `${brandName().toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'shade'}-${colorName.toLowerCase().replace(/\s+/g, '-')}.png`, { type: 'image/png' });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          title: `${brandName()} — ${colorName}`,
          text: `Check out this ${productLabel} in ${colorName} from ${brandName()} ${shareUrl()}`,
          files: [file],
        });
        track();
        setShowMenu(false);
        return;
      } catch (err) {
        if (err.name === 'AbortError') { setShowMenu(false); return; }
      }
    }
    /* Fallback: copy image to clipboard */
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setCopied(true);
      toast.success('Image copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* Final fallback: download */
      handleDownload();
    }
    setShowMenu(false);
  }, [capture, colorName, productLabel, handleDownload, shareUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Email share */
  const handleEmail = useCallback(() => {
    const subject = encodeURIComponent(`${brandName()} — ${colorName} ${productLabel}`);
    const body = encodeURIComponent(
      `Hi,\n\nI'd like to share this shade preview from ${brandName()}:\n\n` +
      `Fabric: ${colorName}\n` +
      `Product: ${productLabel}\n` +
      (config?.width && config?.height ? `Size: ${config.width}" x ${config.height}"\n` : '') +
      `\nSee it here: ${shareUrl()}\n\n` +
      `Best regards`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
    track();
    setShowMenu(false);
  }, [colorName, productLabel, config, shareUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Copy link to configurator */
  const handleCopyLink = useCallback(() => {
    const url = shareUrl();
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    });
    track();
    setShowMenu(false);
  }, [shareUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasColor = !!selectedColor;

  return (
    <div className={`relative ${className}`}>
      <div ref={captureRef}>
        {children}
      </div>

      {/* Floating share bar — hidden when child enters fullscreen */}
      {hasColor && !hideBar && (
        <div className="absolute top-12 left-3 z-40 flex items-center gap-1.5" data-testid="share-preview-bar">
          <button
            type="button"
            onClick={handleDownload}
            disabled={capturing}
            className="w-9 h-9 rounded-lg bg-white/90 hover:bg-white shadow-md flex items-center justify-center text-neutral-700 hover:text-neutral-900 transition-all backdrop-blur-sm"
            title="Download preview"
            data-testid="share-download-btn"
          >
            {capturing ? (
              <div className="w-4 h-4 border-2 border-neutral-400 border-t-neutral-900 rounded-full animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
          </button>

          <div className="relative">
            <button
              type="button"
              onClick={() => setShowMenu(!showMenu)}
              disabled={capturing}
              className="w-9 h-9 rounded-lg bg-amber-500 hover:bg-amber-400 shadow-md flex items-center justify-center text-neutral-900 transition-all"
              title="Share preview"
              data-testid="share-open-btn"
            >
              <Share2 className="w-4 h-4" />
            </button>

            {/* Share menu dropdown */}
            {showMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowMenu(false)} />
                <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-neutral-200 py-1.5 min-w-[180px] z-40" data-testid="share-menu">
                  <button
                    onClick={handleShare}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                    data-testid="share-native-btn"
                  >
                    <Share2 className="w-4 h-4 text-neutral-500" />
                    Share image
                  </button>
                  <button
                    onClick={handleEmail}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                    data-testid="share-email-btn"
                  >
                    <Mail className="w-4 h-4 text-neutral-500" />
                    Send by email
                  </button>
                  <button
                    onClick={handleCopyLink}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                    data-testid="share-copy-link-btn"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-neutral-500" />}
                    Copy link
                  </button>
                  <div className="border-t border-neutral-100 my-1" />
                  <button
                    onClick={() => setShowMenu(false)}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-neutral-400 hover:bg-neutral-50 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SharePreview;
